import { ActivityLogItem } from './activityLogType';
import { ProjectOngoingClaimRequest } from './claimType';
import Concern from './concernType';
import { LineItems } from './lineItemTypes';
import { Amount } from './moneyType';
import ResourceType from './resourceType';

export function isProjectResource(resource: any): resource is ProjectType {
    return (
        resource !== undefined &&
        'type' in resource &&
        resource.type === ResourceType.PROJECT
    );
}

export enum ProjectType {
    SOLAR = 'SOLAR',
    ELECTRICIAN = 'ELECTRICIAN',
}

export enum TypeName {
    SOLAR = 'Solceller',
    ELECTRICIAN = 'Elektriker',
}

export enum ProjectStage {
    UP_FRONT_PAYMENT = 'UP_FRONT_PAYMENT',
    INSPECTION = 'INSPECTION',
    PHYSICAL_INSPECTION = 'PHYSICAL_INSPECTION',
    OFFER = 'OFFER',
    APPLICATION = 'APPLICATION',
    INSTALLATION = 'INSTALLATION',
    EVALUATION = 'EVALUATION',
    PAYMENT = 'PAYMENT',
}

export enum StageName {
    UP_FRONT_PAYMENT = 'Forhåndsbetaling',
    INSPECTION = 'Befaring',
    PHYSICAL_INSPECTION = 'Fysisk befaring',
    OFFER = 'Tilbud',
    APPLICATION = 'Søknad',
    INSTALLATION = 'Montering',
    EVALUATION = 'Evaluering',
    PAYMENT = 'Betaling',
}

export enum ProjectStatus {
    SCHEDULED = 'SCHEDULED',
    UNSCHEDULED = 'UNSCHEDULED',
    CANCELLED = 'CANCELLED',
    AWAITING_REPORT = 'AWAITING_REPORT',
    DRAFT = 'DRAFT',
    AWAITING_APPROVAL = 'AWAITING_APPROVAL',
    EXPIRED = 'EXPIRED',
    AWAITING_SUBMISSION = 'AWAITING_SUBMISSION',
    DECLINED = 'DECLINED',
    IN_REVIEW = 'IN_REVIEW',
    AWAITING_EVALUATION = 'AWAITING_EVALUATION',
    AWAITING_PAYMENT = 'AWAITING_PAYMENT',
    PAID = 'PAID',
    INVOICED = 'INVOICED',
}

export enum ProjectUpForPaymentStageStatus {
    AWAITING_PAYMENT = ProjectStatus.AWAITING_PAYMENT,
}

export enum ProjectInspectionStageStatus {
    UNSCHEDULED = ProjectStatus.UNSCHEDULED,
    SCHEDULED = ProjectStatus.SCHEDULED,
    AWAITING_REPORT = ProjectStatus.AWAITING_REPORT,
    EXPIRED = ProjectStatus.EXPIRED,
    CANCELLED = ProjectStatus.CANCELLED,
}

export interface ProjectInspectionStage {
    stage: ProjectStage.INSPECTION | ProjectStage.PHYSICAL_INSPECTION;
    status: ProjectInspectionStageStatus;
}

export enum ProjectOfferStageStatus {
    DRAFT = ProjectStatus.DRAFT,
    AWAITING_APPROVAL = ProjectStatus.AWAITING_APPROVAL,
    DECLINED = ProjectStatus.DECLINED,
    EXPIRED = ProjectStatus.EXPIRED,
}

export interface ProjectOfferStage {
    stage: ProjectStage.OFFER;
    status: ProjectOfferStageStatus;
}

export enum ProjectApplicationStageStatus {
    AWAITING_SUBMISSION = ProjectStatus.AWAITING_SUBMISSION,
    IN_REVIEW = ProjectStatus.IN_REVIEW,
    DECLINED = ProjectStatus.DECLINED,
}

export interface ProjectApplicationStage {
    stage: ProjectStage.APPLICATION;
    status: ProjectApplicationStageStatus;
}

export enum ProjectInstallationStageStatus {
    SCHEDULED = ProjectStatus.SCHEDULED,
    UNSCHEDULED = ProjectStatus.UNSCHEDULED,
    CANCELLED = ProjectStatus.CANCELLED,
}

export interface ProjectInstallationStage {
    stage: ProjectStage.INSTALLATION;
    status: ProjectInstallationStageStatus;
}

export enum ProjectEvaluationStageStatus {
    AWAITING_EVALUATION = ProjectStatus.AWAITING_EVALUATION,
}

export interface ProjectEvaluationStage {
    stage: ProjectStage.EVALUATION;
    status: ProjectEvaluationStageStatus;
}

export enum ProjectPaymentStageStatus {
    AWAITING_PAYMENT = ProjectStatus.AWAITING_PAYMENT,
    PAID = ProjectStatus.PAID,
    INVOICED = ProjectStatus.INVOICED,
}

export interface ProjectPaymentStage {
    stage: ProjectStage.PAYMENT;
    status: ProjectPaymentStageStatus;
}

export enum ProjectUpFrontPaymentStageStatus {
    AWAITING_PAYMENT = ProjectStatus.AWAITING_PAYMENT,
}

export interface ProjectUpFrontPaymentStage {
    stage: ProjectStage.UP_FRONT_PAYMENT;
    status: ProjectUpFrontPaymentStageStatus;
}

interface ContactInfo {
    name: string;
    phoneNumber: string;
    email: string;
    uuid: string;
}

interface SellerInfo extends ContactInfo {
    warehouse: string;
}

interface CustomerInfo extends ContactInfo {
    address: string;
    projectCount?: number;
}

interface CaseworkerInfo extends ContactInfo {
    address: string;
    flags: any[];
    permissions: any[];
}

interface ContractorInfo extends Omit<ContactInfo, 'email'> {
    address: string;
    postArea: string;
    postCode: string;
}

export interface CompanyInfo {
    id?: number;
    partnerId?: string;
    address: string;
    name: string;
    organizationNumber: string;
    postCode: string;
    postArea: string;
}

interface DepartmentInfo extends Omit<ContactInfo, 'email' | 'uuid'> {
    address: string;
    id: number;
    lat: number;
    lng: number;
    mainContact: any;
    notificationEmails: Array<{ email: string; notifications: string[] }>;
    phoneNumber: string;
    postArea: string;
    postCode: string;
    storeEmployees: CaseworkerInfo[];
}

export interface ElectricianInfo extends ContactInfo {
    id: number;
}

export interface ProjectTotals {
    discount: Amount;
    extraCostFromEveningHours: Amount;
    extraCostFromWeekendHours: Amount;
    materialListCost: Amount;
    workHours: number;
    workHoursRange: string;
    roundedWorkHours: number;
    subtotal: Amount;
    total: Amount;
    totalBeforeDiscount: Amount;
}

interface ProjectFlags {
    haveDraft: boolean;
    customerContactRequested: boolean;
    haveReferredAFriend: boolean;
    haveOngoingEvaluationComplaint: boolean;
    offerRenewalRequested: boolean;
}

interface ActiveDate {
    label: string;
    date: string;
}

interface Project {
    id: number;
    uuid: string;
    reference: string;
    type: string;
    projectType: ProjectType;
    status: ProjectStatus;
    statusChangedAt: string;
    stage: ProjectStage;
    name: string;
    createdAt: string;
    activeDate?: ActiveDate;
    // customer & instalation addres
    address: string;
    customer: CustomerInfo;
    place: string;
    postCode: string;
    company?: CompanyInfo;
    // installation dates
    requestedInstallationDate?: string | null;
    installationCompletedAt?: string | null;
    installationDate?: string | null;
    installationDateEnd?: string | null;
    // staff info
    caseworker?: CaseworkerInfo;
    contractor?: ContractorInfo;
    department?: DepartmentInfo;
    electrician?: ElectricianInfo;
    sellerDescription?: string;
    seller?: SellerInfo;
    salesPartnerSeller?: {
        name: string;
        phoneNumber: string;
        email: string;
        partner: {
            id: number;
            name: string;
        };
        department: {
            id: number;
            name: string;
        };
    };
    // money & time
    total: Amount;
    totals: ProjectTotals;
    totalPrice: number;
    hourlyRate: number | null;
    electricianPayout: Amount;
    roundedWorkHours: number;
    discountPerExtraJob: Amount | null;
    // instalation line items
    lineItems: LineItems;
    // meta
    concerns: Concern[];
    additionalInfo: any;
    flags: ProjectFlags;
    activityLog: ActivityLogItem[];
    createdBySpoton: boolean;
    // optional subentities
    draft?: any;
    cart?: any;
    solarApplication?: any;
    inspection?: any;
    physicalInspection?: any;
    offer?: any;
    initialOrder?: any;
    claim?: any;
    evaluation?: any;
    payment?: any;
    reservation?: any;
    awaitingCustomerContact?: any;
    vismaOrders?: number[];
    invoicedAt?: string | null;
    solarUnscheduledExpiresAt?: string | null;
    stageStatus:
        | ProjectUpFrontPaymentStage
        | ProjectInspectionStage
        | ProjectOfferStage
        | ProjectApplicationStage
        | ProjectInstallationStage
        | ProjectEvaluationStage
        | ProjectPaymentStage;

    electricianInfo: any[];
    customerBriefing: any[];
    ongoingClaimRequest: ProjectOngoingClaimRequest;
}

export interface SolarProject extends Project {
    cart: {
        bookedAt: string;
        lineItems: LineItems;
        totals: ProjectTotals;
        uuid: string;
    };
    type: ProjectType.SOLAR;
    upFrontPayment?: {
        capturedAt?: string;
        confirmedAt?: string;
        createdAt: string;
        deletedAt?: string;
        id: number;
        initiatedAt?: string;
        processor: 'Klarna';
        projectId: number;
        status: ProjectUpFrontPaymentStageStatus;
        updatedAt: string;
        data: {
            order: {
                total: number;
                taxTotal: number;
                totalDiscount: number;
            };
        };
    };
}

export default Project;
